import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '../components/header';
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import ContactForm from '../components/ContactForm';
import SpecialtyDoctorsCarousel from '../components/SpecialtyDoctorsCarousel';
import TestimonialVideoCarousel from '../components/TestimonialVideoCarousel';
import BlogsCarousel from '../components/BlogsCarousel';
import { getDoctorsBySpeciality } from '../utils/doctorUtils';
import api from '../services/api';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const BestDoctorDetails = () => {
  const query = useQuery();
  const speciality = query.get('speciality') || 'Best Doctors in India';
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormIndex, setShowFormIndex] = useState(null);
  const [dataSource, setDataSource] = useState('');

  useEffect(() => {
    const fetchDoctors = async () => {
      // Extract country from the speciality string (e.g., "Best Heart Doctors in India" -> "India")
      // or determine from the referrer URL
      let country = 'India'; // default
      
      const countryMatch = speciality.match(/in (.+)$/);
      if (countryMatch) {
        country = countryMatch[1];
      } else {
        // Try to determine country from referrer URL
        const referrer = document.referrer;
        if (referrer.includes('/bestdoctorsinthailand')) {
          country = 'Thailand';
        } else if (referrer.includes('/bestdoctorsinturkey')) {
          country = 'Turkey';
        } else if (referrer.includes('/bestdoctorsinsouthkorea')) {
          country = 'South Korea';
        } else if (referrer.includes('/bestdoctorsinindia')) {
          country = 'India';
        }
        // If we still don't have a country and speciality doesn't include "in Country",
        // we can also check for URL parameters
        const urlCountry = query.get('country');
        if (urlCountry) {
          country = urlCountry;
        }
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Use the same API service that other components use successfully
        try {
          // First try to get doctors from the speciality endpoint
          try {
            const specialityResponse = await api.get('/doctors/by-speciality', { 
              params: { 
                speciality: speciality,
                country: country,
                limit: 100 
              } 
            });
            
            if (specialityResponse.data.doctors && specialityResponse.data.doctors.length > 0) {
              const formattedDoctors = specialityResponse.data.doctors.map(doctor => ({
                id: doctor.id || doctor._id,
                name: doctor.name,
                specialty: doctor.specialty,
                experience: doctor.experience || '10+ Years',
                image: doctor.image || '/images/doctor-placeholder.jpg',
                description: doctor.description || doctor.about || 'Experienced medical professional providing top-quality healthcare services.',
                qualifications: Array.isArray(doctor.qualifications) 
                  ? doctor.qualifications 
                  : typeof doctor.qualifications === 'string' 
                    ? doctor.qualifications.split(',').map(q => q.trim())
                    : [],
                hospital: doctor.hospital || 'Leading Medical Center',
                country: doctor.country,
                whatsappLink: `https://api.whatsapp.com/send/?phone=9599222655&text=Hello%2C+I+am+interested+in+consulting+Dr.+${encodeURIComponent(doctor.name)}+for+${encodeURIComponent(speciality)}.+Please+contact+me.&type=phone_number&app_absent=0`,
                link: `/doctor-details/${doctor.id || createDoctorSlug(doctor.name)}`
              }));
              setDoctors(formattedDoctors);
              setDataSource(`API (Speciality Endpoint - ${specialityResponse.data.doctors.length} doctors)`);
              setError(null);
              return;
            }
          } catch (specialityError) {
            console.warn('Speciality endpoint failed, trying general doctors endpoint:', specialityError.message);
          }

          // Fallback to general doctors endpoint
          const response = await api.get('/doctors', { 
            params: { limit: 100 } 
          });
          
          let allDoctors = [];
          
          // Handle API response format
          if (response.data.doctors && Array.isArray(response.data.doctors)) {
            allDoctors = response.data.doctors;
          } else if (Array.isArray(response.data)) {
            allDoctors = response.data;
          }
          
          // Extract the speciality from the display name for filtering
          const extractSpeciality = (displaySpeciality) => {
            const match = displaySpeciality.match(/Best (.+) Doctors in/);
            return match ? match[1] : displaySpeciality;
          };
          
          const searchSpeciality = extractSpeciality(speciality);
          
          // Filter by country and speciality
          const filteredDoctors = allDoctors.filter(doctor => 
            // Filter by country
            doctor.country && doctor.country.toLowerCase() === country.toLowerCase() &&
            // Filter by speciality
            doctor.specialty && 
            doctor.specialty.toLowerCase().includes(searchSpeciality.toLowerCase())
          );
          
          // If we have doctors from the database, format them for display
          if (filteredDoctors.length > 0) {
            const formattedDoctors = filteredDoctors.map(doctor => ({
              id: doctor.id || doctor._id,
              name: doctor.name,
              specialty: doctor.specialty,
              experience: doctor.experience || '10+ Years',
              image: doctor.image || '/images/doctor-placeholder.jpg',
              description: doctor.description || doctor.about || 'Experienced medical professional providing top-quality healthcare services.',
              qualifications: Array.isArray(doctor.qualifications) 
                ? doctor.qualifications 
                : typeof doctor.qualifications === 'string' 
                  ? doctor.qualifications.split(',').map(q => q.trim())
                  : [],
              hospital: doctor.hospital || 'Leading Medical Center',
              country: doctor.country,
              whatsappLink: `https://api.whatsapp.com/send/?phone=9599222655&text=Hello%2C+I+am+interested+in+consulting+Dr.+${encodeURIComponent(doctor.name)}+for+${encodeURIComponent(speciality)}.+Please+contact+me.&type=phone_number&app_absent=0`,
              link: `/doctor-details/${doctor.id || createDoctorSlug(doctor.name)}`
            }));
            setDoctors(formattedDoctors);
            setDataSource(`API (Live Database - ${filteredDoctors.length} of ${allDoctors.length} doctors)`);
          } else {
            throw new Error(`No doctors found with speciality: ${speciality} in ${country}`);
          }
          
        } catch (apiError) {
          console.warn('API call failed, using local data:', apiError.message);
          // Use local data as fallback
          const localDoctors = getDoctorsBySpeciality(speciality, country);
          if (localDoctors.length > 0) {
            setDoctors(localDoctors);
            setDataSource('Local Data');
            setError('API temporarily unavailable, showing data from local cache');
          } else {
            setDoctors([]);
            setDataSource('No Data');
            setError(`No doctors found for ${speciality} in ${country}`);
          }
        }
        
      } catch (err) {
        console.error('Error in fetchDoctors:', err);
        setError('Unable to load doctor data');
        setDataSource('Error');
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [speciality]);

  const getSpecialityName = (speciality) => {
    // Extract the speciality name from the full string
    const match = speciality.match(/Best (.+) Doctors in/);
    return match ? match[1] : 'medical';
  };

  const getCountryName = (speciality) => {
    const match = speciality.match(/in (.+)$/);
    return match ? match[1] : 'India';
  };

  // Function to create URL-friendly slug from doctor name
  const createDoctorSlug = (doctorName) => {
    return doctorName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim('-'); // Remove leading/trailing hyphens
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-grow flex flex-col items-center py-16 px-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1976f2]"></div>
            <p className="mt-4 text-gray-600">Loading doctors...</p>
          </div>
        </main>
        <Footer />
      <FloatingChatButton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-grow flex flex-col items-center py-16 px-4">
        <h1 className="text-4xl md:text-5xl font-bold mb-12 text-center" style={{ letterSpacing: '0.03em', lineHeight: '1.2' }}>
          Get the best {getSpecialityName(speciality)} consultation in {getCountryName(speciality)}
        </h1>
        
        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 w-full max-w-3xl text-center">
            <p className="text-yellow-800">
              <strong>Note:</strong> {error}
            </p>
          </div>
        )}
        
        <p className="text-lg text-gray-900 text-center max-w-3xl mb-8" style={{ letterSpacing: '0.01em', lineHeight: '1.7' }}>
          Find the most trusted doctors for {getSpecialityName(speciality).toLowerCase()} consultation in {getCountryName(speciality)}. 
          Our partner doctors offer world-class medical care with extensive experience, proven expertise, and comprehensive treatment options. 
          Compare doctors, read about their qualifications, and book consultations easily.
        </p>
        
        <p className="text-lg text-gray-900 text-center max-w-3xl mb-12" style={{ letterSpacing: '0.01em', lineHeight: '1.7' }}>
          Each doctor is carefully selected based on their expertise in {getSpecialityName(speciality).toLowerCase()}, years of experience, 
          patient satisfaction ratings, and adherence to international medical standards. Get personalized treatment plans and expert medical advice 
          from the best specialists in the field.
        </p>
        
        {doctors.length > 0 ? (
          <div className="w-full max-w-5xl flex flex-col gap-8 mb-12">
            {doctors.map((d, idx) => (
              <div key={d.id || idx} className="border-2 border-blue-300 rounded-xl p-4 flex flex-col md:flex-row items-center gap-6 bg-white shadow-sm">
                <img 
                  src={d.image} 
                  alt={d.name} 
                  className="w-40 h-32 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.src = '/images/doctor-placeholder.jpg';
                  }}
                />
                <div className="flex-1 flex flex-col items-start">
                  <Link 
                    to={d.link}
                    className="text-xl font-bold mb-1 text-[#1976f2] hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    {d.name}
                  </Link>
                  <div className="text-sm text-gray-600 mb-1">{d.specialty} | {d.hospital}</div>
                  <div className="text-sm text-blue-600 mb-2">
                    <strong>Experience:</strong> {d.experience}
                  </div>
                  <div className="text-gray-700 mb-2 line-clamp-3">{d.description}</div>
                  
                  {d.qualifications && d.qualifications.length > 0 && (
                    <div className="text-sm text-blue-600 mb-2">
                      <strong>Qualifications:</strong> {
                        Array.isArray(d.qualifications) 
                          ? d.qualifications.slice(0, 3).join(', ')
                          : typeof d.qualifications === 'string' 
                            ? d.qualifications.split(',').slice(0, 3).join(', ')
                            : d.qualifications
                      }
                      {(Array.isArray(d.qualifications) && d.qualifications.length > 3) || 
                       (typeof d.qualifications === 'string' && d.qualifications.split(',').length > 3) 
                        ? '...' : ''}
                    </div>
                  )}
                  
                  <div className="flex items-center mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="text-yellow-400 text-lg">★</span>
                    ))}
                    <span className="ml-3 text-sm font-semibold text-gray-700">Top Rated Doctor</span>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <a
                      href={d.whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#25D366] text-white px-4 py-2 rounded font-semibold flex items-center gap-2 hover:bg-[#128C7E] transition"
                    >
                      <i className="fab fa-whatsapp text-lg"></i> Chat Now
                    </a>
                    <button
                      className="bg-[#1976f2] text-white px-4 py-2 rounded font-semibold hover:bg-blue-600 transition"
                      onClick={() => setShowFormIndex(idx === showFormIndex ? null : idx)}
                    >
                      Book Consultation
                    </button>
                  </div>
                  {showFormIndex === idx && (
                    <div className="mt-6 w-full">
                      <ContactForm formType="doctor-inquiry" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full max-w-5xl text-center py-12">
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-gray-700 mb-4">
                No doctors found for {getSpecialityName(speciality)} in {getCountryName(speciality)}
              </h3>
              <p className="text-gray-600 mb-6">
                We're constantly adding new doctors to our network. 
                In the meantime, you can contact us for personalized doctor recommendations.
              </p>
              <div className="flex justify-center gap-4">
                <a
                  href="https://api.whatsapp.com/send/?phone=9599222655&text=Hello%2C+I+am+looking+for+doctors+for+consultation.+Please+help+me.&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#25D366] text-white px-6 py-3 rounded font-semibold flex items-center gap-2 hover:bg-[#128C7E] transition"
                >
                  <i className="fab fa-whatsapp text-lg"></i> Contact Us
                </a>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Carousels Section */}
      <div className="w-full flex flex-col items-center gap-16 pb-16">
        <section className="container mx-auto px-4 py-16">
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="text-[#1976f2]">Other Top Doctors</span> for {getSpecialityName(speciality)}
          </h2>
          <SpecialtyDoctorsCarousel 
            specialties={[getSpecialityName(speciality)]} 
            treatmentTitle={getSpecialityName(speciality)}
            currentDoctorIds={doctors.map(d => d.id).filter(Boolean)}
          />
        </section>
        <TestimonialVideoCarousel />
        <BlogsCarousel />
      </div>
      <Footer />
    </div>
  );
};

export default BestDoctorDetails;
